import { toast } from "react-toastify";

/** @const {number} */
export const UNDO_WINDOW_MS = 8000;

const undoButtonStyle = {
  padding: 0,
  border: 0,
  background: "transparent",
  color: "inherit",
  fontWeight: 600,
  textDecoration: "underline",
  cursor: "pointer",
};

/**
 * Shows "<message> — Undo" for a few seconds. Exactly one of `onUndo` or
 * `onCommit` runs: `onUndo` if the user clicks Undo, otherwise `onCommit` when
 * the window ends, the toast is closed, or the caller calls `flush()` (for
 * example because the user left the page).
 *
 * @param {{
 *   message: string,
 *   onUndo: function(),
 *   onCommit?: function(),
 *   timeoutMs?: number,
 *   className?: string,
 * }} args
 * @return {{flush: function(), undo: function()}}
 */
export const showUndoToast = ({
  message,
  onUndo,
  onCommit,
  timeoutMs = UNDO_WINDOW_MS,
  className,
}) => {
  let settled = false;
  let timer = null;
  let toastId = null;

  const commit = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    onCommit?.();
  };

  const undo = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    onUndo();
    toast.dismiss(toastId);
  };

  timer = window.setTimeout(() => {
    commit();
    toast.dismiss(toastId);
  }, timeoutMs);

  toastId = toast.info(
    <span className={className}>
      {`${message} — `}
      <button type="button" onClick={undo} style={undoButtonStyle}>
        Undo
      </button>
    </span>,
    {
      autoClose: timeoutMs,
      closeOnClick: false,
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      onClose: commit,
    },
  );

  return {
    flush: () => {
      commit();
      toast.dismiss(toastId);
    },
    undo,
  };
};
